/**
 * 瓦片几何与 URL 展开 —— 地图引擎里**唯一**一套"经纬度 ↔ 瓦片编号"的换算。
 *
 * ## 为什么自己算瓦片编号
 *
 * 离线能力的核心问题是"**哪一块**地图我没缓存"，而这个问题的答案只能落在
 * 瓦片编号上（缓存键、覆盖判定、预缓存清单都要用它）。MapLibre 内部也在算同一套
 * 公式，但它不对外暴露"当前视野覆盖哪几块瓦片"，所以这里必须有一份独立实现 ——
 * 因此它必须是**纯函数**且被单测覆盖：一旦与 MapLibre 的算法不一致，
 * 界面会自信地报告"已缓存"，而屏幕上却是空白。
 *
 * 用的是标准 Web Mercator（EPSG:3857）滑动瓦片编号：`x` 向东、`y` 向南，
 * 原点在左上角。纬度按 ±85.05112878° 截断（墨卡托在这里发散，
 * 再往极区走 `y` 会趋向无穷），这也正是所有在线底图的实际覆盖范围。
 */

import type { GeoBounds, LatLon } from '@hgc/ardupilot';

/** 瓦片坐标（XYZ，`y` 向南增长）。 */
export interface TileCoord {
  readonly z: number;
  readonly x: number;
  readonly y: number;
}

/** 一段连续的瓦片编号区间（同一个 `z`）。 */
export interface TileRange {
  readonly z: number;
  readonly minX: number;
  readonly maxX: number;
  readonly minY: number;
  readonly maxY: number;
}

/** 自定义 URL 协议名。样式里的底图源一律指向 `hgc://`，由我们自己决定去缓存还是去网络。 */
export const HGC_TILE_SCHEME = 'hgc';

/** 墨卡托可表示的纬度上限。 */
export const WEB_MERCATOR_MAX_LAT_DEG = 85.0511287798066;

/** 底图源允许的缩放范围。 */
export const MIN_TILE_ZOOM = 0;
export const MAX_TILE_ZOOM = 22;

/** 一次物化瓦片列表的默认上限：防止有人在缩放到 0 时要求"整张世界地图"而把浏览器拖死。 */
export const DEFAULT_TILE_LIMIT = 4096;

/** `tms` 方案（部分离线瓦片包）把 `y` 从下往上数，与 XYZ 差一次翻转。 */
export type TileScheme = 'xyz' | 'tms';

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

/** 某一层有多少块瓦片（`2^z`）。 */
export function tileCountAtZoom(zoom: number): number {
  const z = clampTileZoom(zoom, MIN_TILE_ZOOM, MAX_TILE_ZOOM);
  return 2 ** z;
}

/** 把任意缩放值夹进 `[min, max]` 并取整。`NaN` 落到 `min`：宁可显示最粗的一层，也不要抛错。 */
export function clampTileZoom(zoom: number, min: number, max: number): number {
  const lo = clampTileZoomInput(min);
  const hi = Math.max(lo, clampTileZoomInput(max));
  if (!Number.isFinite(zoom)) return lo;
  return clamp(Math.floor(zoom), lo, hi);
}

function clampTileZoomInput(value: number): number {
  if (!Number.isFinite(value)) return MIN_TILE_ZOOM;
  return clamp(Math.floor(value), MIN_TILE_ZOOM, MAX_TILE_ZOOM);
}

/** 把经度规整到 `[-180, 180)`。 */
export function wrapLongitude(lonDeg: number): number {
  if (!Number.isFinite(lonDeg)) return 0;
  return ((lonDeg % 360) + 540) % 360 - 180;
}

/** 经纬度 → 瓦片编号。纬度先截断到墨卡托上限，越界的经度按环绕处理。 */
export function lonLatToTile(point: LatLon, zoom: number): TileCoord {
  const z = clampTileZoom(zoom, MIN_TILE_ZOOM, MAX_TILE_ZOOM);
  const count = 2 ** z;
  const lon = wrapLongitude(point.lonDeg);
  const lat = clamp(point.latDeg, -WEB_MERCATOR_MAX_LAT_DEG, WEB_MERCATOR_MAX_LAT_DEG);

  const x = clamp(Math.floor(((lon + 180) / 360) * count), 0, count - 1);
  const latRad = (lat * Math.PI) / 180;
  const mercatorY = (1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2;
  const y = clamp(Math.floor(mercatorY * count), 0, count - 1);
  return { z, x, y };
}

function latDegAtTileEdge(y: number, count: number): number {
  const n = Math.PI - (2 * Math.PI * y) / count;
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
}

/** 瓦片编号 → 该瓦片的西北角经纬度。 */
export function tileNorthWest(tile: TileCoord): LatLon {
  const count = 2 ** tile.z;
  return { latDeg: latDegAtTileEdge(tile.y, count), lonDeg: (tile.x / count) * 360 - 180 };
}

/**
 * 瓦片编号 → 地理包围盒。
 *
 * `crossesAntimeridian` 恒为 `false`：最东一列瓦片的东经恰好是 180°，
 * 那是"经度 180"这个点，不是"跨到 -180"那一类需要拆成两段的情形。
 */
export function tileBounds(tile: TileCoord): GeoBounds {
  const count = 2 ** tile.z;
  return {
    southDeg: latDegAtTileEdge(tile.y + 1, count),
    northDeg: latDegAtTileEdge(tile.y, count),
    westDeg: (tile.x / count) * 360 - 180,
    eastDeg: ((tile.x + 1) / count) * 360 - 180,
    crossesAntimeridian: false,
  };
}

/**
 * 覆盖缓冲盒所需的瓦片区间。
 *
 * 跨 180° 经线时返回**两段**（西段与东段）—— 这是唯一能正确表示的方式：
 * 把 `west > east` 当成一段会得到"几乎整个地球"，那正是"地图上看着正常、
 * 实际请求了几万块瓦片"的那类错误。
 */
export function tileRangesForBounds(bounds: GeoBounds, zoom: number): readonly TileRange[] {
  const z = clampTileZoom(zoom, MIN_TILE_ZOOM, MAX_TILE_ZOOM);
  const south = clamp(bounds.southDeg, -WEB_MERCATOR_MAX_LAT_DEG, WEB_MERCATOR_MAX_LAT_DEG);
  const north = clamp(bounds.northDeg, -WEB_MERCATOR_MAX_LAT_DEG, WEB_MERCATOR_MAX_LAT_DEG);
  const topTile = lonLatToTile({ latDeg: north, lonDeg: 0 }, z);
  const bottomTile = lonLatToTile({ latDeg: south, lonDeg: 0 }, z);
  const minY = Math.min(topTile.y, bottomTile.y);
  const maxY = Math.max(topTile.y, bottomTile.y);

  const segments: readonly (readonly [number, number])[] = bounds.crossesAntimeridian
    ? [
        [bounds.westDeg, 180],
        [-180, bounds.eastDeg],
      ]
    : [[bounds.westDeg, bounds.eastDeg]];

  const ranges: TileRange[] = [];
  for (const [west, east] of segments) {
    if (!Number.isFinite(west) || !Number.isFinite(east)) continue;
    const first = xAtLongitude(clamp(west, -180, 180), z);
    // 东边界按**闭区间**处理：经度落在瓦片左边缘时，那块瓦片也在视野里
    const last = xAtLongitude(clamp(east, -180, 180), z);
    ranges.push({
      z,
      minX: Math.min(first, last),
      maxX: Math.max(first, last),
      minY,
      maxY,
    });
  }
  return ranges;
}

/**
 * 经度 → `x` 编号。**不做环绕**。
 *
 * 这里不能借用 `lonLatToTile`：它按 `[-180, 180)` 的惯例把 180° 折到 -180°（同一个物理位置，
 * 但在瓦片表里是最东一列与最西一列）。跨经线的窄带恰好以 180° 结尾，一折就会变成
 * `minX = 0, maxX = count - 1` —— 也就是"几乎整个地球"，而那正是覆盖判定里
 * "报告说已缓存、屏幕上是空白"的来源。
 */
function xAtLongitude(lonDeg: number, z: number): number {
  const count = 2 ** z;
  return clamp(Math.floor(((lonDeg + 180) / 360) * count), 0, count - 1);
}

/** 区间里有几块瓦片。 */
export function countTiles(ranges: readonly TileRange[]): number {
  let total = 0;
  for (const range of ranges) {
    total += (range.maxX - range.minX + 1) * (range.maxY - range.minY + 1);
  }
  return total;
}

/**
 * 把区间物化成瓦片列表，最多 `limit` 块（按行优先截断）。
 *
 * 截断是**故意**的：低缩放下一个视野可以覆盖几十万块瓦片，而调用方
 * （覆盖判定、预缓存）只想知道前若干块 —— 与其抛错，不如让它拿到
 * "前 N 块"，由调用方自己决定要不要继续。
 */
export function tilesInRanges(
  ranges: readonly TileRange[],
  limit = DEFAULT_TILE_LIMIT,
): readonly TileCoord[] {
  const tiles: TileCoord[] = [];
  for (const range of ranges) {
    for (let y = range.minY; y <= range.maxY; y += 1) {
      for (let x = range.minX; x <= range.maxX; x += 1) {
        if (tiles.length >= limit) return tiles;
        tiles.push({ z: range.z, x, y });
      }
    }
  }
  return tiles;
}

/**
 * 选出"最细但不超过 `maxTiles` 块"的缩放层。
 *
 * 覆盖判定必须在一个**可行的**层级上做：按当前视野的原始缩放去比对，
 * 低缩放下会要求几十万块瓦片，结论永远是"几乎全部缺失"，而那既不正确
 * （野外看全局时有低层级瓦片就够了）也没法读。
 */
export function coarsestZoomForBounds(
  bounds: GeoBounds,
  maxTiles: number,
  zoomRange: { readonly min: number; readonly max: number },
): number {
  const min = clampTileZoom(zoomRange.min, MIN_TILE_ZOOM, MAX_TILE_ZOOM);
  const max = clampTileZoom(zoomRange.max, min, MAX_TILE_ZOOM);
  let chosen = min;
  for (let z = min; z <= max; z += 1) {
    if (countTiles(tileRangesForBounds(bounds, z)) > maxTiles) {
      // 这一层已经太多：退回上一层（上一层就是 min 时只能接受 min）
      return Math.max(min, z - 1);
    }
    chosen = z;
  }
  return chosen;
}

/** 缓存键。用 `/` 分隔而不是 `:`，日志里能直接读。 */
export function tileKey(sourceId: string, tile: TileCoord): string {
  return `${sourceId}/${tile.z}/${tile.x}/${tile.y}`;
}

/** `tileKey` 的逆运算；格式不对返回 `null`（缓存里可能有上个版本留下的异物）。 */
export function parseTileKey(
  key: string,
): { readonly sourceId: string; readonly tile: TileCoord } | null {
  const match = /^([a-z0-9][a-z0-9._-]*)\/(\d+)\/(\d+)\/(\d+)$/.exec(key);
  if (match === null) return null;
  const z = Number(match[2]);
  const x = Number(match[3]);
  const y = Number(match[4]);
  const count = 2 ** z;
  if (!Number.isInteger(z) || x < 0 || y < 0 || x >= count || y >= count) return null;
  return { sourceId: match[1] ?? '', tile: { z, x, y } };
}

/**
 * 样式里给 MapLibre 用的瓦片模板。
 *
 * 关键点：**不指向任何真实服务器**，而是指向我们注册的 `hgc://` 协议 ——
 * 这样每个瓦片请求都会先经过"查缓存 / 决定要不要联网"这一段逻辑。
 * 若不这么做，MapLibre 会自己直接发 HTTP 请求，于是浏览器缓存成了唯一一层缓存：
 * 它容量不可控、离线时不可靠，而且我们**无法知道**当前视野缺哪些瓦片。
 */
export function hgcTileTemplate(sourceId: string, scheme: TileScheme = 'xyz'): string {
  return `${HGC_TILE_SCHEME}://tiles/${sourceId}/{z}/{x}/{y}${schemeQuery(scheme)}`;
}

/** 单块瓦片的 `hgc://` URL（协议处理器与测试直接用）。 */
export function hgcTileUrl(sourceId: string, tile: TileCoord, scheme: TileScheme = 'xyz'): string {
  return `${HGC_TILE_SCHEME}://tiles/${sourceId}/${tile.z}/${tile.x}/${tile.y}${schemeQuery(scheme)}`;
}

function schemeQuery(scheme: TileScheme): string {
  return scheme === 'tms' ? '?scheme=tms' : '';
}

/**
 * 解析 `hgc://tiles/<源>/<z>/<x>/<y>`。
 *
 * 之所以能从 URL 反推瓦片编号，是因为这个 URL 是**我们自己**生成的：
 * 真实底图的 URL 形态千奇百怪（`{z}/{y}/{x}` 倒序、WMTS 的 `TILEROW/TILECOL` 查询串、
 * 圆角 `@2x` 后缀……），从真实 URL 里猜瓦片编号必然出错，
 * 而错误的缓存键会让"报告已缓存"和"实际缓存的东西"悄悄分家。
 */
export function parseHgcTileUrl(
  url: string,
): { readonly sourceId: string; readonly tile: TileCoord; readonly scheme: TileScheme } | null {
  const match = /^hgc:\/\/tiles\/([^/?#]+)\/(\d+)\/(\d+)\/(\d+)(?:\?([^#]*))?$/.exec(url);
  if (match === null) return null;
  const z = Number(match[2]);
  const x = Number(match[3]);
  const y = Number(match[4]);
  const count = 2 ** z;
  if (!Number.isInteger(z) || x < 0 || y < 0 || x >= count || y >= count) return null;
  return {
    sourceId: match[1] ?? '',
    tile: { z, x, y },
    scheme: /(?:^|&)scheme=tms(?:&|$)/.test(match[5] ?? '') ? 'tms' : 'xyz',
  };
}

/** 子域轮转，与 MapLibre 的 `(x + y) % subdomains.length` 保持一致。 */
export function subdomainFor(tile: TileCoord, subdomains: readonly string[]): string | null {
  if (subdomains.length === 0) return null;
  return subdomains[Math.abs(tile.x + tile.y) % subdomains.length] ?? null;
}

export interface ExpandTileUrlOptions {
  readonly scheme?: TileScheme;
  readonly subdomains?: readonly string[];
  /** 需要密钥的源由用户自填；缺失时返回 `null`，而不是拼出一个必然 401 的 URL。 */
  readonly key?: string | null;
  /** 设备像素比标记：模板里写 `{ratio}` 的源（部分商业源）需要它。 */
  readonly pixelRatio?: number;
}

/**
 * 把真实底图模板（例如 `https://tile.openstreetmap.org/{z}/{x}/{y}.png`）
 * 展开成一块具体瓦片的 URL。
 *
 * 有占位符没能被替换（典型是 `{key}` 而没有密钥）时返回 `null`：
 * 拼出一个带空参数的 URL 只会得到 401/403，而在野外的表现是"地图一片空白" ——
 * 那是最难排查的一类失败，因为它看起来和"这块区域没数据"一模一样。
 *
 * 判据是"还剩任何 `{占位符}"，而不是只认我们已知的那几个：自填源里写错的
 * `{foo}` 一旦被原样发出去，上游只会 404，而 404 会被记成"这里没有瓦片"并**算进已缓存**——
 * 于是覆盖判定会自信地报告"这片区域可用"，而屏幕上是空白。
 */
export function expandTileUrl(
  template: string,
  tile: TileCoord,
  options: ExpandTileUrlOptions = {},
): string | null {
  const scheme = options.scheme ?? 'xyz';
  const key = options.key ?? null;
  if (template.includes('{key}') && (key === null || key === '')) return null;

  const subdomain = subdomainFor(tile, options.subdomains ?? []);
  const pixelRatio = options.pixelRatio ?? 1;
  const expanded = template
    .replace(/\{z\}/g, String(tile.z))
    .replace(/\{x\}/g, String(tile.x))
    .replace(/\{y\}/g, String(scheme === 'tms' ? 2 ** tile.z - tile.y - 1 : tile.y))
    .replace(/\{s\}/g, subdomain ?? '')
    .replace(/\{ratio\}/g, pixelRatio > 1 ? '@2x' : '')
    .replace(/\{key\}/g, key ?? '');

  return /\{[a-zA-Z][a-zA-Z0-9]*\}/.test(expanded) ? null : expanded;
}
